import React, { Component, type ErrorInfo, ReactNode } from 'react'

import { isValidCorrelationId } from '@/utils/correlationId'
import { getSearchQueryAttributes } from '@/utils/newRelic/getSearchQueryAttributes'

interface ErrorBoundaryProps {
  children?: ReactNode
  correlationId?: string
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (globalThis.newrelic?.noticeError) {
      const { correlationId } = this.props

      globalThis.newrelic.noticeError(error, {
        componentStack: errorInfo.componentStack ?? '',
        reportingSource: 'react-error-boundary',
        ...(isValidCorrelationId(correlationId) && { correlationId }),
        ...getSearchQueryAttributes(globalThis.location.search),
      })
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div>
          <style>
            {`html, body {
                width: 100%;
                height: 100%;
                padding: 0;
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
            }`}
          </style>
          <h1>Oops, there is an error!</h1>
          <p>
            Let's start a{' '}
            <a href="/search" style={{ color: 'cornflowerblue' }}>
              new search
            </a>
            .
          </p>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
